import { type MiddlewareFunction, type Params } from 'react-router';

import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import { navigate } from '@trello/router/navigate';
import { isMeAlias } from '@trello/urls';

import { getCurrentUsername } from './getCurrentUsername';

interface MeAliasRedirectOptions {
  createRedirectUrl: (username: string) => string;
  getUsernameFromParams: (params: Params<string>) => string | undefined;
}

/**
 * Redirect to the login page if the user is not logged in.
 */
export const createMeAliasRedirect =
  ({
    createRedirectUrl,
    getUsernameFromParams,
  }: MeAliasRedirectOptions): MiddlewareFunction =>
  async ({ params }): Promise<Response | void> => {
    const username = getUsernameFromParams(params);
    if (!username) {
      return; // No username dynamic segment in URL to check
    }

    // If the username is a me alias, then redirect to the appropriate place
    if (isMeAlias(username)) {
      const loginUrl = `/login?returnUrl=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`;

      const currentUsername = await getCurrentUsername();

      if (!currentUsername) {
        window.location.assign(loginUrl);
        // Return a 401 response to stop further processing of the route
        return new Response(null, {
          status: 401,
        });
      }

      const redirectUrl = createRedirectUrl(
        encodeURIComponent(dangerouslyConvertPrivacyString(currentUsername)),
      );
      navigate(redirectUrl, { replace: true });
      // Return a 401 response to stop further processing of the route
      return new Response(null, {
        status: 401,
      });
    }
  };
